import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/insights", label: "Insights" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/75 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between h-24 lg:h-28 gap-4">
        <Link
          to="/"
          data-testid="brand-logo-arabic"
          className="flex items-center group shrink-0"
          aria-label={BRAND.arabic}
        >
          <img
            src={BRAND.logoArabic}
            alt={BRAND.arabic}
            style={{
              mixBlendMode: "screen",
              filter: "brightness(1.55) saturate(1.35) contrast(1.08) drop-shadow(0 0 10px rgba(91,74,200,0.35))",
            }}
            className="h-16 lg:h-20 w-auto object-contain group-hover:opacity-90 transition-opacity"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium transition-colors relative ${
                  isActive
                    ? "text-[#d4af37]"
                    : "text-white/70 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {n.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#d4af37]" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/"
          data-testid="brand-logo-english"
          className="hidden sm:flex items-center group shrink-0"
          aria-label={BRAND.full}
        >
          <img
            src={BRAND.logo}
            alt={BRAND.full}
            style={{
              mixBlendMode: "screen",
              filter: "brightness(1.55) saturate(1.35) contrast(1.08) drop-shadow(0 0 10px rgba(238,106,45,0.3))",
            }}
            className="h-16 lg:h-20 w-auto object-contain group-hover:opacity-90 transition-opacity"
          />
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-white/5 ${
                    isActive ? "text-[#d4af37]" : "text-white/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-contact-cta"
              className="mt-3 inline-flex items-center justify-center bg-[#d4af37] text-black py-3 font-semibold"
            >
              Talk to us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
